/** @format */

import {useCallback, useEffect, useState} from 'react';

import {Box, Button, LinearProgress, Typography} from '@mui/material';
import Alert from '@mui/material/Alert';
import {styled} from '@mui/material/styles';
import {Trans, useTranslation} from 'react-i18next';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import DemoModal from 'components/DemoModal';
import InformationDialog from 'components/dialogs/InformationDialog';

import {useMixpanelContext} from 'contexts/MixpanelContext';

import {authUrl} from 'helpers/connectHelpers';
import {useCallbackOnReturn} from 'helpers/routerHelpers';

import SidebarEdit from 'components/MediaplanSidebar/SidebarEdit';

import BackToProjectButton from 'components/BackToProjectButton';

import {OauthProvider as Provider} from 'graphql/generated/types';

import StepWrapper from '../../StepWrapper';

import NameStep from '../../NameStep';
import TokenStep from '../../TokenStep';
import AdSystemConnectBlock from '../../components/AdSystemConnectBlock';
import AnalyticsSettings from '../components/AnalyticsSettings';
import MetrikaConnectBlock, {MetrikaStepHeader} from '../../components/MetrikaConnectBlock';
import {STATS_PROVIDERS} from '../Created';
import {
  useDeleteProviderTokenMutation,
  useDisconnectProviderMutation,
  useGetCurrentUserQuery,
  useUpdateCompanyMutation,
} from './graphql/__generated__';

const PREFIX = 'CREATE';

const classes = {
  deleteProjectButton: `${PREFIX}-deleteProjectButton`,
  deleteProjectButtonIcon: `${PREFIX}-deleteProjectButtonIcon`,
};

const StyledBox = styled(Box)(({theme}) => ({
  [`& .${classes.deleteProjectButton}`]: {
    padding: theme.spacing(5, 4),
    borderRadius: '8px',
    background: '#EA5759',
    color: '#F1F1F1',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '16px',
    height: '48px',
  },
  [`& .${classes.deleteProjectButtonIcon}`]: {
    marginRight: theme.spacing(2),
    color: '#F1F1F1',
    fontSize: '16px',
  },
}));

const Edit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {t} = useTranslation();

  const {mixpanel} = useMixpanelContext();

  let [searchParams, setSearchParams] = useSearchParams();

  const isEdit = searchParams.get('edit');

  const companyId = searchParams.get('companyId');
  const confirm = searchParams.get('confirm');

  const [isOnConfirmFired, setIsOnConfirmFired] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [saveCompanyError, setSaveCompanyError] = useState('');
  const [activeProvider, setActiveProvider] = useState<Provider | null>('YANDEX');

  const [showErrorDialog, setShowErrorDialog] = useState<boolean>(false);
  const [showDemoModal, setShowDemoModal] = useState(false);

  const {loading, error, data, refetch} = useGetCurrentUserQuery({
    variables: {id: companyId},
  });

  const [updateCompany] = useUpdateCompanyMutation();
  const [disconnectProvider, {loading: loadingDisconnectProvider}] =
    useDisconnectProviderMutation();
  const [deleteProviderToken] = useDeleteProviderTokenMutation();

  useCallbackOnReturn(refetch);

  const deleteConfirm = useCallback(() => {
    if (searchParams.has('confirm')) {
      searchParams.delete('confirm');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!data?.currentUser) return;

    const {company} = data.currentUser;

    if (!company?.tokens.length) return;

    const token = company.tokens.find(token => token.provider === confirm);

    if (token) {
      if (isOnConfirmFired) return;

      const {provider, lastError} = token;
      const isConnected = lastError == null;

      if (!isConnected) return;

      setIsOnConfirmFired(true);

      deleteConfirm();

      navigate(`subaccounts?companyId=${companyId}&provider=${provider}`, {
        state: {background: location},
      });
    }
  }, [
    data,
    confirm,
    companyId,
    location,
    navigate,
    isOnConfirmFired,
    setSearchParams,
    searchParams,
    deleteConfirm,
  ]);

  useEffect(() => {
    if (!data?.currentUser) return;

    const {company} = data.currentUser;
    setProjectName(company?.name || '');
  }, [data]);

  useEffect(() => {
    if (!data?.currentUser) return;

    const {company} = data.currentUser;

    const token = company?.tokens.find(token => token.provider === confirm);
    const {lastError} = token || {};

    if (token && lastError) {
      setShowErrorDialog(true);
    }
  }, [data, confirm]);

  if (error) {
    return <Alert severity='error'>{error.message}</Alert>;
  }
  if (loading || loadingDisconnectProvider || !data?.currentUser) {
    return <LinearProgress style={{flex: 1}} />;
  }

  const {company, roles} = data.currentUser;
  const tokens = company?.tokens || [];

  const adTokens = tokens.filter(token => !STATS_PROVIDERS.includes(token.provider));
  const statTokens = tokens.filter(token => STATS_PROVIDERS.includes(token.provider));

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setProjectName(e.target.value);

      mixpanel.track('click_wizard_update_company_button');

      if (!companyId) {
        return;
      }

      await updateCompany({
        variables: {id: companyId, name: e.target.value},
      });
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unknown error.';
      setSaveCompanyError(message);
    }
  };

  const getState = (provider: Provider) => {
    const url = new URL(window.location.href);
    url.searchParams.append('confirm', provider);

    const returnUrl = url.toString();
    const state = {companyId, returnUrl};

    return window.btoa(JSON.stringify(state));
  };

  const handleAdConnect = (provider: Provider) => {
    if (roles.includes('DEMO')) {
      setShowDemoModal(true);
      return;
    }

    mixpanel.track('click_wizard_before_connect_continue_button', {
      provider,
    });

    const authUrlResult = authUrl(provider, getState(provider));

    if (!authUrlResult) return;

    window.location.href = authUrlResult.href;
  };

  const handleEditSubbaccounts = (provider: Provider) => {
    if (roles.includes('DEMO')) {
      setShowDemoModal(true);
      return;
    }

    navigate(`subaccounts?companyId=${companyId}&provider=${provider}`, {
      state: {background: location},
    });
  };

  const handleRemoveToken = async (provider: Provider) => {
    if (!companyId) {
      return;
    }

    if (roles.includes('DEMO')) {
      setShowDemoModal(true);
      return;
    }

    mixpanel.track('click_disconnect_button', {provider: provider});

    await disconnectProvider({variables: {provider, companyId}});

    refetch();
  };

  const renderErrorDialog = () => {
    if (!data || !companyId) return;

    const token = company?.tokens.find(token => token.provider === confirm);

    if (!token) return;

    const handleClose = async () => {
      setShowErrorDialog(false);

      await deleteProviderToken({
        variables: {provider: token.provider, companyId: companyId},
      });

      deleteConfirm();

      refetch();
    };

    return (
      <InformationDialog
        open={showErrorDialog}
        onClose={handleClose}
        title={t(
          'Ошибка при подключении рекламной кампании',
          'Ошибка при подключении рекламной кампании',
        )}
        body={token.lastError}
      />
    );
  };

  const handleCloseDemoModal = () => {
    setShowDemoModal(false);
  };

  const handleProjectDelete = async () => {
    navigate(`/home/project/confirm-delete-company?companyId=${companyId}`);
  };

  const tokensHaveAccounts = tokens.length > 0 ? tokens.every(t => t.accounts.length > 0) : false;

  const handleSaveAll = () => {
    navigate('/');
  };

  return (
    <StyledBox>
      <SidebarEdit
        activeStep='CREATE'
        enableAll
        projectName={projectName}
        onSaveAll={tokensHaveAccounts ? handleSaveAll : null}
      />
      <Typography variant='h1'>
        <Trans>Редактирование проекта</Trans>
      </Typography>
      <NameStep
        onChange={handleChange}
        saveCompanyError={saveCompanyError}
        projectName={projectName}
        isProjectCreated
        isEditPage
      />
      <StepWrapper
        header={
          <>
            2. <Trans>Рекламная система</Trans>
          </>
        }
      >
        {adTokens.length > 0 ? (
          adTokens.map(token => (
            <TokenStep
              key={token.provider}
              token={token}
              onEditSubbaccounts={handleEditSubbaccounts}
              onRemoveToken={handleRemoveToken}
              onAdConnect={handleAdConnect}
            />
          ))
        ) : (
          <AdSystemConnectBlock
            provider={activeProvider ?? 'YANDEX'}
            onConnect={handleAdConnect}
            activeProvider={activeProvider}
            onProviderClick={setActiveProvider}
          />
        )}
      </StepWrapper>
      {!activeProvider && (
        <Box sx={{mt: 12}}>
          <Typography>
            <Trans>Без рекламной системы проект будет ограничен в функционале</Trans>
          </Typography>
        </Box>
      )}
      {(adTokens.length > 0 || !activeProvider) && (
        <StepWrapper header={<MetrikaStepHeader />}>
          {statTokens.length > 0 ? (
            statTokens.map(token => (
              <TokenStep
                key={token.provider}
                token={token}
                onEditSubbaccounts={handleEditSubbaccounts}
                onRemoveToken={handleRemoveToken}
                onAdConnect={handleAdConnect}
                isAnalyticsStep
              />
            ))
          ) : (
            <MetrikaConnectBlock onConnect={handleAdConnect} />
          )}
          {statTokens.some(t => t.provider === 'METRIKA') && companyId && (
            <AnalyticsSettings companyId={companyId} />
          )}
        </StepWrapper>
      )}
      <Box sx={{mt: 16, mb: 24}}>
        <Button className={classes.deleteProjectButton} onClick={handleProjectDelete}>
          <DeleteOutlineIcon className={classes.deleteProjectButtonIcon} />
          <Trans>Удалить проект</Trans>
        </Button>
        {isEdit && <BackToProjectButton />}
      </Box>
      {renderErrorDialog()}
      {showDemoModal && <DemoModal onClose={handleCloseDemoModal} />}
    </StyledBox>
  );
};

export default Edit;
